import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSEO } from "../hooks/useSEO";
import Countdown from "../components/Countdown";
import FollowBlock from "../components/FollowBlock";

function VotingClosed({ opensAt, ceremonyDate }) {
  const now = Date.now();
  const target = opensAt ? new Date(opensAt).getTime() : null;
  const isBeforeOpen = target && now < target;

  return (
    <div className="pt-[140px] pb-40 max-w-[1300px] mx-auto px-6 lg:px-12" data-testid="vote-closed">
      <p className="label mb-8" style={{ color: "#C9A961" }}>Prix du Public — Vote 2026</p>
      <h1
        className="font-serif mb-16"
        style={{ fontSize: "clamp(48px, 8vw, 120px)", lineHeight: 0.92, letterSpacing: "-0.025em", color: "#F8F6F0" }}
      >
        {isBeforeOpen ? "Bientôt." : "Terminé."}
      </h1>

      <div style={{ maxWidth: 720 }}>
        {isBeforeOpen ? (
          <>
            <p className="label mb-10" style={{ color: "#A1A1AA" }}>
              Les votes ouvrent 90 jours avant la cérémonie. Le scrutin démarre dans :
            </p>
            <Countdown targetIso={opensAt} />
            {ceremonyDate && (
              <p className="label mt-16" style={{ color: "#A1A1AA" }}>
                Cérémonie prévue le{" "}
                <span style={{ color: "#F8F6F0" }}>
                  {new Date(ceremonyDate).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </p>
            )}
          </>
        ) : (
          <p style={{ color: "rgba(248,246,240,0.7)", fontSize: 18, lineHeight: 1.8 }}>
            La période de vote est close. Les résultats seront annoncés lors de la cérémonie.
          </p>
        )}
      </div>
      <FollowBlock
        title={isBeforeOpen ? "Le vote ouvre bientôt." : "Restez connecté·e."}
        subtitle={isBeforeOpen
          ? "Inscrivez-vous pour être averti·e dès l'ouverture du scrutin."
          : "Suivez les annonces officielles et le palmarès lors de la cérémonie."}
      />
    </div>
  );
}

export default function Vote() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "";
  const initialNominee = searchParams.get("nominee") || "";

  useSEO("Voter", "Exprimez votre voix pour le Prix du Public. Un vote par catégorie, validé par email et numéro de téléphone.");

  const [votingStatus, setVotingStatus] = useState(null); // null = loading
  const [categories, setCategories] = useState([]);
  const [nominees, setNominees] = useState([]);
  const [step, setStep] = useState(1);
  const [categorySlug, setCategorySlug] = useState(initialCategory);
  const [nomineeId, setNomineeId] = useState(initialNominee);
  const [form, setForm] = useState({ voter_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    (async () => {
      const [statusRes, catRes] = await Promise.allSettled([
        api.get("/voting-status"),
        api.get("/categories"),
      ]);
      if (statusRes.status === "fulfilled") setVotingStatus(statusRes.value.data);
      else setVotingStatus({ open: false, opens_at: null, ceremony_date: null });
      if (catRes.status === "fulfilled") {
        setCategories(catRes.value.data);
        if (!initialCategory && catRes.value.data.length) setCategorySlug(catRes.value.data[0].slug);
      }
    })();
  }, [initialCategory]);

  useEffect(() => {
    if (!categorySlug) return;
    (async () => {
      const { data } = await api.get("/nominees", { params: { category_slug: categorySlug } });
      setNominees(data);
      if (!data.find((n) => n.id === nomineeId)) setNomineeId("");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug]);

  const currentCategory = useMemo(() => categories.find((c) => c.slug === categorySlug), [categories, categorySlug]);
  const currentNominee = useMemo(() => nominees.find((n) => n.id === nomineeId), [nominees, nomineeId]);

  const onNext = () => {
    if (!nomineeId) { setError("Veuillez choisir un·e nominé·e."); return; }
    setError("");
    setStep(2);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/vote", {
        category_slug: categorySlug,
        nominee_id: nomineeId,
        voter_name: form.voter_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      setConfirmation({ vote_id: data.vote_id, category: currentCategory, nominee: currentNominee });
      setStep(3);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'envoi du vote.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading
  if (votingStatus === null) {
    return (
      <div className="pt-[140px] pb-40 max-w-[1300px] mx-auto px-6 lg:px-12">
        <p className="label" style={{ color: "#A1A1AA" }}>Chargement…</p>
      </div>
    );
  }

  // Votes fermés
  if (!votingStatus.open) {
    return <VotingClosed opensAt={votingStatus.opens_at} ceremonyDate={votingStatus.ceremony_date} />;
  }

  return (
    <div className="pt-[140px] pb-40 max-w-[1300px] mx-auto px-6 lg:px-12" data-testid="vote-page">
      <p className="label mb-8" style={{ color: "#C9A961" }}>Prix du Public — Vote 2026</p>
      <h1
        className="font-serif mb-16"
        style={{ fontSize: "clamp(48px, 8vw, 120px)", lineHeight: 0.92, letterSpacing: "-0.025em", color: "#F8F6F0" }}
        data-testid="vote-title"
      >
        Votre voix.
      </h1>

      {/* Progress */}
      <div className="flex items-center gap-6 mb-16" data-testid="vote-progress">
        {[1, 2, 3].map((i) => (
          <React.Fragment key={i}>
            <div className="transition-colors duration-500" style={{ width: 10, height: 10, background: step >= i ? "#C9A961" : "#1c1c1c" }} aria-current={step === i} />
            {i < 3 && <div style={{ flex: 1, height: 1, background: step > i ? "#C9A961" : "#1c1c1c" }} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div data-testid="vote-step-1">
          <p className="label mb-4">Étape 01 / Votre catégorie</p>
          <div className="flex flex-wrap gap-3 mb-16">
            {categories.map((c) => (
              <button key={c.slug} onClick={() => setCategorySlug(c.slug)} className="label transition-colors duration-500 px-4 py-3"
                style={{ border: "1px solid " + (c.slug === categorySlug ? "#C9A961" : "#1c1c1c"), color: c.slug === categorySlug ? "#C9A961" : "#F8F6F0", background: "transparent", cursor: "pointer" }}
                data-testid={`vote-cat-${c.slug}`}>
                {c.title}
              </button>
            ))}
          </div>

          <p className="label mb-6">Étape 02 / Votre choix</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {nominees.map((n) => {
              const selected = n.id === nomineeId;
              return (
                <button key={n.id} onClick={() => setNomineeId(n.id)} type="button" className="text-left transition-colors duration-500"
                  style={{ background: "transparent", border: "1px solid " + (selected ? "#C9A961" : "#1c1c1c"), padding: 24, cursor: "pointer" }}
                  data-testid={`vote-nominee-${n.id}`}>
                  <div className="flex gap-6">
                    <div style={{ width: 120, aspectRatio: "3/4", background: "#111", flexShrink: 0 }}>
                      <img src={n.portrait} alt={n.name} className="bw-image w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-serif" style={{ fontSize: 28, lineHeight: 1.1, color: "#F8F6F0" }}>{n.name}</h3>
                        <p className="font-serif italic mt-1" style={{ color: "#C9A961", fontSize: 17 }}>« {n.work} »</p>
                      </div>
                      <p className="label mt-4" style={{ color: selected ? "#C9A961" : "#A1A1AA" }}>{selected ? "Sélectionné" : "Choisir"}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {error && <p className="label mt-8" style={{ color: "#C9A961" }}>{error}</p>}
          <div className="mt-14 flex justify-end">
            <button onClick={onNext} className="btn-solid" disabled={!nomineeId} data-testid="vote-next">Continuer →</button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-12" data-testid="vote-step-2">
          <div className="md:col-span-5">
            <p className="label mb-6">Votre choix</p>
            <p className="font-serif" style={{ fontSize: 32, lineHeight: 1.15, color: "#F8F6F0" }}>{currentNominee?.name}</p>
            <p className="font-serif italic mt-2" style={{ color: "#C9A961", fontSize: 20 }}>« {currentNominee?.work} »</p>
            <p className="label mt-6">Catégorie</p>
            <p className="font-serif mt-2" style={{ fontSize: 22, color: "#F8F6F0" }}>{currentCategory?.title}</p>
            <button type="button" className="label mt-10" onClick={() => setStep(1)} style={{ color: "#A1A1AA", borderBottom: "1px solid #A1A1AA", paddingBottom: 4 }} data-testid="vote-back">
              ← Modifier mon choix
            </button>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-10">
            <p className="label">Étape 03 / Vos coordonnées</p>
            <p style={{ color: "rgba(248,246,240,0.7)", fontSize: 15, lineHeight: 1.75 }}>
              Le vote exige un email et un numéro de téléphone. Cette double authentification prévient les abus et garantit l'intégrité du scrutin. Un seul vote par catégorie est comptabilisé.
            </p>
            <div>
              <label htmlFor="voter-name" className="label block mb-3">Nom complet</label>
              <input id="voter-name" required value={form.voter_name} onChange={(e) => setForm((f) => ({ ...f, voter_name: e.target.value }))} className="input-editorial" placeholder="Prénom Nom" data-testid="vote-input-name" />
            </div>
            <div>
              <label htmlFor="voter-email" className="label block mb-3">Adresse email</label>
              <input id="voter-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="input-editorial" placeholder="prenom.nom@exemple.fr" data-testid="vote-input-email" />
            </div>
            <div>
              <label htmlFor="voter-phone" className="label block mb-3">Téléphone</label>
              <input id="voter-phone" type="tel" required value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="input-editorial" placeholder="+33 6 00 00 00 00" data-testid="vote-input-phone" />
            </div>
            {error && <p className="label" style={{ color: "#C9A961" }} data-testid="vote-error">{error}</p>}
            <button type="submit" className="btn-solid" disabled={submitting} data-testid="vote-submit">
              {submitting ? "Envoi…" : "Confirmer mon vote"}
            </button>
          </div>
        </form>
      )}

      {/* STEP 3 */}
      {step === 3 && confirmation && (
        <div data-testid="vote-step-3" className="max-w-3xl">
          <p className="label mb-8" style={{ color: "#C9A961" }}>Confirmation</p>
          <h2 className="font-serif" style={{ fontSize: "clamp(40px, 6vw, 80px)", lineHeight: 1.02, color: "#F8F6F0" }}>
            Merci. Votre voix a été consignée.
          </h2>
          <p className="mt-10" style={{ color: "rgba(248,246,240,0.75)", fontSize: 17, lineHeight: 1.8 }}>
            Vous avez voté pour{" "}
            <span className="font-serif italic" style={{ color: "#C9A961" }}>{confirmation.nominee?.name}</span>{" "}
            dans la catégorie <span className="font-serif" style={{ color: "#F8F6F0" }}>{confirmation.category?.title}</span>.
            Un reçu vous a été adressé par courrier électronique.
          </p>
          <p className="label mt-10" style={{ color: "#A1A1AA" }}>Référence de vote</p>
          <p className="font-serif mt-2" style={{ fontSize: 22, color: "#F8F6F0" }} data-testid="vote-reference">{confirmation.vote_id}</p>
          <div className="mt-14 flex gap-6">
            <button className="btn-ghost" onClick={() => { setStep(1); setConfirmation(null); setNomineeId(""); setForm({ voter_name: "", email: "", phone: "" }); }} data-testid="vote-another">
              Voter dans une autre catégorie
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
